(ns practitest.frontend.components2.dashboard.charts.pie-render
  (:require ["d3" :as d3]
            [practitest.frontend.components2.dashboard.charts.pie-simulation :as sim]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- calc-radius
  "Calculate pie radius based on config."
  [{:keys [width height radius-multiplier]}]
  (* (/ (min width height) 2) radius-multiplier))

(defn- arc-at-radius
  "D3 arc generator with both radii set to r.
   (.centroid (arc-at-radius r) d) returns [x,y] at distance r
   from center along the slice's mid-angle."
  [r]
  (-> (d3/arc) (.innerRadius r) (.outerRadius r)))

(defn- centroid
  "Returns [x y] at distance r from center along slice's mid-angle."
  [^js arc ^js d]
  (let [c (.centroid arc d)]
    [(aget c 0) (aget c 1)]))

(defn- label-key
  "Key function for D3 data join — keys by label for stable enter/update/exit."
  [^js d]
  (.. d -data -label))

(defn- hidden-label? [^js d]
  (not (aget (.-data d) "show-label")))

(defn- css-escape
  "Escape a string for safe use inside a CSS attribute selector value."
  [s]
  (js/CSS.escape s))

(defn- zero-arc
  "Arc datum collapsed to a single angle (zero-width)."
  [angle]
  #js {:startAngle angle :endAngle angle})

(defn- arc-tween
  "Interpolator for attrTween that transitions between two arc states."
  [arc-gen from to]
  (let [interp (d3/interpolate from to)]
    (fn [t] (arc-gen (interp t)))))

(defn- exit-arc-tween [arc-gen]
  (fn [^js d]
    (arc-tween arc-gen d (zero-arc (.-endAngle d)))))

(defn- enter-arc-tween [arc-gen]
  (fn [^js d]
    (arc-tween arc-gen (zero-arc (.-startAngle d)) d)))

(defn- update-arc-tween [arc-gen]
  (fn [^js d]
    (this-as ^js this
      (let [prev (or (.-_prev this) d)]
        (set! (.-_prev this) d)
        (arc-tween arc-gen prev d)))))

(defn- calc-polyline-points
  "Calculate polyline points string for a datum."
  [^js d polyline-arc label-positions]
  (let [label       (.. d -data -label)
        [ax ay]     (centroid polyline-arc d)
        {:keys [x y]} (get label-positions label)]
    (str ax "," ay " " x "," y)))

(defn- label-transform
  "Calculate transform string for a label group."
  [^js d label-positions]
  (let [label (.. d -data -label)
        {:keys [x y]} (get label-positions label)]
    (str "translate(" x "," y ")")))


(defn- hover-handler [hover-arc hover?]
  (fn [^js _event ^js d]
    (this-as this
      (let [escaped     (css-escape (.. d -data -label))
            hidden      (hidden-label? d)
            group       (-> d3 (.select (.-parentNode this)))
            chart-body  (-> d3 (.select (.-parentNode (.-parentNode this))))
            chart-group (-> d3 (.select (.-parentNode (.-parentNode (.-parentNode this)))))
            transform   (if hover?
                          (let [[dx dy] (centroid hover-arc d)]
                            (str "translate(" dx "px," dy "px)"))
                          "translate(0,0)")]
        (-> group
            (.select "path.slice")
            (.style "transform" transform))
        (-> chart-body
            (.selectAll (str ".edge-group path.edge[data-index=\"" escaped "\"]"))
            (.style "transform" transform))
        (-> chart-group
            (.select (str "polyline.connector[data-index=\"" escaped "\"]"))
            (.classed "hover" hover?))
        (-> chart-group
            (.select (str "g.label-group[data-index=\"" escaped "\"] .label-content"))
            (.classed "hover" hover?))
        (when hidden
          (-> chart-group
              (.select (str "g.label-group[data-index=\"" escaped "\"]"))
              (.classed "hidden" (not hover?)))
          (-> chart-group
              (.select (str "polyline.connector[data-index=\"" escaped "\"]"))
              (.classed "hidden" (not hover?))))))))

;; ============================================================================
;; Setup
;; ============================================================================

(defn- setup-svg!
  "Initialize SVG element for rendering."
  [svg-element {:keys [width height center-y-offset]}]
  (.setAttribute svg-element "viewBox" (str "0 0 " width " " height))
  (let [d3-svg    (.select d3 svg-element)
        existing  (.select d3-svg "g.chart-group")
        transform (str "translate(" (/ width 2) "," (- (/ height 2) center-y-offset) ")")]
    (if (.empty existing)
      (-> d3-svg
          (.append "g")
          (.attr "class" "chart-group")
          (.attr "transform" transform))
      existing)))

(defn- pie-gen [pad-angle]
  (-> d3
      (.pie)
      (.sort nil)
      (.padAngle pad-angle)
      (.value (fn [d] (.-value d)))))

(defn- create-arc-generator [inner-radius outer-radius]
  (-> d3
      (.arc)
      (.innerRadius inner-radius)
      (.outerRadius outer-radius)))

;; ============================================================================
;; Renderers
;; ============================================================================


(defn- render-edge-layer!
  "Render a single edge layer at a given offset and darkness."
  [svg {:keys [transition-duration animation-duration]}
   {:keys [data-ready radius]}
   {:keys [dx dy radius-extra]} idx]
  (let [edge-arc      (create-arc-generator 0 (+ radius radius-extra))
        color-fn      (fn [^js d] (.. d -data -color))
        class-name (str "edge-group-" idx)
        edge-g     (let [existing (.select svg (str "g." class-name))]
                     (if (.empty existing)
                       (-> svg
                           (.insert "g" ":first-child")
                           (.attr "class" (str "edge-group " class-name))
                           (.attr "transform" (str "translate(" dx "," dy ")")))
                       existing))
        edges      (-> edge-g
                       (.selectAll "path.edge")
                       (.data data-ready label-key))
        enter      (-> (.enter edges)
                       (.append "path")
                       (.attr "class" "edge")
                       (.attr "data-index" label-key)
                       (.attr "fill" color-fn))]

    ;; EXIT
    (-> (.exit edges)
        (.transition)
        (.duration transition-duration)
        (.attrTween "d" (exit-arc-tween edge-arc))
        (.remove))

    ;; ENTER
    (-> enter
        (.each (fn [^js d] (this-as ^js this (set! (.-_prev this) d))))
        (.transition)
        (.duration animation-duration)
        (.ease d3/easeCubicOut)
        (.attrTween "d" (enter-arc-tween edge-arc)))

    ;; UPDATE
    (-> edges
        (.attr "fill" color-fn)
        (.transition)
        (.duration transition-duration)
        (.attrTween "d" (update-arc-tween edge-arc)))))

(defn- render-edge!
  "Render multiple stacked edge layers for a smooth 3D thickness effect."
  [svg {:keys [edge-layers edge-depth] :as config} ctx]
  (doseq [[idx layer] (map-indexed vector edge-layers)]
    (render-edge-layer! svg config ctx
                        (-> layer
                            (update :dy * edge-depth)
                            (update :radius-extra * edge-depth))
                        idx)))

(defn- render-slices!
  [svg {:keys [hover-offset on-slice-click
               animation-duration transition-duration enter-ease]}
   {:keys [data-ready arc-generator]}]
  (let [hover-arc  (arc-at-radius hover-offset)
        color-fn   (fn [^js d] (.. d -data -color))
        groups     (-> svg
                       (.selectAll "g.slice-group")
                       (.data data-ready label-key))
        enter-g    (-> (.enter groups)
                       (.append "g")
                       (.attr "class" "slice-group"))
        ;; Visible slice — moves on hover, no pointer events
        enter-slice (-> enter-g
                        (.append "path")
                        (.attr "class" "slice")
                        (.attr "data-index" label-key)
                        (.attr "fill" color-fn)
                        (.style "pointer-events" "none"))
        ;; Invisible hit area — stays in place, catches events
        enter-hit   (-> enter-g
                        (.append "path")
                        (.attr "class" "slice-hit")
                        (.attr "fill" "transparent"))
        merged-g    (.merge enter-g groups)
        slices      (.select merged-g "path.slice")
        hits        (.select merged-g "path.slice-hit")]

    ;; EXIT groups
    (-> (.exit groups)
        (.transition)
        (.duration transition-duration)
        (.style "opacity" 0)
        (.remove))

    ;; ENTER — animate visible slices
    (-> enter-slice
        (.each (fn [^js d] (this-as ^js this (set! (.-_prev this) d))))
        (.transition)
        (.duration animation-duration)
        (.ease enter-ease)
        (.attrTween "d" (enter-arc-tween arc-generator)))

    ;; ENTER — set hit area to match
    (-> enter-hit
        (.each (fn [^js d] (this-as ^js this (set! (.-_prev this) d))))
        (.transition)
        (.duration animation-duration)
        (.ease enter-ease)
        (.attrTween "d" (enter-arc-tween arc-generator)))

    ;; UPDATE — visible slices (.select propagates parent datum to child)
    (-> (.select groups "path.slice")
        (.transition)
        (.duration transition-duration)
        (.attrTween "d" (update-arc-tween arc-generator))
        (.attr "fill" color-fn))

    ;; UPDATE — hit areas
    (-> (.select groups "path.slice-hit")
        (.transition)
        (.duration transition-duration)
        (.attrTween "d" (update-arc-tween arc-generator)))

    ;; Events on hit areas
    (-> hits
        (.on "mouseover" (hover-handler hover-arc true))
        (.on "mouseout"  (hover-handler hover-arc false))
        (.on "click" #(when on-slice-click
                        (on-slice-click (js->clj (.-data %2) :keywordize-keys true)))))))


(defn- render-polylines!
  [svg {:keys [polyline-start-multiplier fade-duration fade-delay transition-duration]}
   {:keys [data-ready radius label-positions]}]
  (let [polyline-arc (arc-at-radius (* radius polyline-start-multiplier))
        points-fn    (fn [^js d] (calc-polyline-points d polyline-arc label-positions))
        lines      (-> svg
                       (.selectAll "polyline.connector")
                       (.data data-ready label-key))
        enter      (-> (.enter lines)
                       (.append "polyline")
                       (.attr "class" "connector")
                       (.attr "data-index" label-key)
                       (.classed "hidden" hidden-label?)
                       (.attr "points" points-fn))]

    ;; EXIT
    (-> (.exit lines)
        (.transition)
        (.duration transition-duration)
        (.style "opacity" 0)
        (.remove))

    ;; ENTER
    (-> enter
        (.transition)
        (.delay fade-delay)
        (.duration fade-duration)
        (.style "opacity" 1))

    ;; UPDATE
    (-> lines
        (.classed "hidden" hidden-label?)
        (.transition)
        (.duration transition-duration)
        (.attr "points" points-fn))))

(defn- render-labels!
  "Render pie chart labels using SVG foreignObject elements.

   Each label is a <g> group translated to coordinates from the force
   simulation (label-positions), in the chart-group's centered coordinate
   system (origin = chart center). Inside each group a <foreignObject>
   embeds an HTML div, enabling CSS text truncation and flexbox alignment
   that plain SVG <text> cannot provide.

   foreignObject sizing & positioning:
   All coordinates below are group-local (the <g> is already at the anchor).
   The chart origin is the center, so SVG edges are at x = ±(width/2).

   The foreignObject always stretches from the anchor toward the nearest
   SVG edge, giving the label maximum space to render text:

     fo-width  = (width/2 - |x|) + label-padding
     fo-height = 50 (hardcoded)
     fo-y      = -25 (vertically centered on anchor)

    Left-side (x < 0)                Right-side (x > 0)
    fo-x = -fo-width                fo-x = 0

    SVG edge              anchor    anchor              SVG edge
    |                        |      |                        |
    |   <─── fo-width ───>   |      |   <─── fo-width ───>  |
    |        [.label .ext]   |      |   [.label .ext]        |
    |                        |      |                        |
    justify: flex-end               justify: flex-start

   Text always hugs the anchor side: flex-end pushes it right (toward
   the anchor on the left side), flex-start pushes it left (toward the
   anchor on the right side).

   HTML structure inside each foreignObject (styled in charts.scss):
   - div.label-content  — flexbox row, nowrap, handles alignment
     - span.label       — primary text, truncates with ellipsis when too long
                          (overflow:hidden + text-overflow:ellipsis + min-width:0)
     - span.label-ext   — secondary text (e.g. count), never truncates
                          (flex-shrink:0), so .label shrinks first

   Font sizing: CSS uses calc(12px / var(--svg-scale, 1)) to keep labels
   at a constant 12px on screen. See `update-svg-scale!` for details.

   D3 enter/update/exit lifecycle:
   - EXIT:   fade out (opacity -> 0 over transition-duration), then remove.
   - ENTER:  create <g>, append foreignObject + HTML content, fade in
             after fade-delay (so labels appear after slice animation).
   - UPDATE: transition <g> to new position, refresh foreignObject width/x,
             text content, and alignment.

   Tooltip: if a tooltip atom is provided, mouseenter/mouseleave handlers
   are attached to the merged selection (enter + existing) that populate
   the atom with {:text, :element} on hover and nil it on leave."
  [svg {:keys [fade-duration fade-delay transition-duration width label-padding]}
   {:keys [data-ready label-positions tooltip]}]
  (let [half-w       (/ width 2)
        fo-height    50
        transform-fn (fn [^js d] (label-transform d label-positions))
        fo-width-fn  (fn [^js d]
                       (let [{:keys [x]} (get label-positions (.. d -data -label))]
                         (+ (- half-w (abs x)) label-padding)))
        fo-x-fn      (fn [^js d]
                       (let [{:keys [right?]} (get label-positions (.. d -data -label))]
                         (if right? 0 (- (fo-width-fn d)))))
        align-fn     (fn [^js d]
                       (let [{:keys [right?]} (get label-positions (.. d -data -label))]
                         (if right? "flex-start" "flex-end")))
        groups       (-> svg
                        (.selectAll "g.label-group")
                        (.data data-ready label-key))
        enter        (-> (.enter groups)
                         (.append "g")
                         (.attr "class" "label-group")
                         (.attr "data-index" label-key)
                         (.classed "hidden" hidden-label?)
                         (.attr "transform" transform-fn))
        merged       (.merge enter groups)]

    ;; EXIT
    (-> (.exit groups)
        (.transition)
        (.duration transition-duration)
        (.style "opacity" 0)
        (.remove))

    ;; ENTER
    (-> enter
        (.transition)
        (.delay fade-delay)
        (.duration fade-duration)
        (.style "opacity" 1))

    ;; ENTER: append foreignObject with label + label-ext spans
    (let [fo  (-> enter
                  (.append "foreignObject")
                  (.attr "class" "label-fo")
                  (.attr "width" fo-width-fn)
                  (.attr "height" fo-height)
                  (.attr "x" fo-x-fn)
                  (.attr "y" (/ fo-height -2)))
          div (-> fo
                  (.append "xhtml:div")
                  (.attr "class" "label-content")
                  (.style "justify-content" align-fn))]
      (-> div
          (.append "xhtml:span")
          (.attr "class" "label")
          (.text (fn [^js d] (.. d -data -label))))
      (-> div
          (.append "xhtml:span")
          (.attr "class" "label-ext")
          (.text (fn [^js d] (aget (.-data d) "label-ext")))))

    ;; Tooltip events
    (when tooltip
      (-> merged
          (.on "mouseenter"
               (fn [^js _event ^js d]
                 (this-as this
                   (reset! tooltip {:text    (.. d -data -tooltip)
                                    :element (.querySelector this ".label")}))))
          (.on "mouseleave"
               (fn [] (reset! tooltip nil)))))

    ;; UPDATE — refresh visibility and position
    (-> groups
        (.classed "hidden" hidden-label?)
        (.transition)
        (.duration transition-duration)
        (.attr "transform" transform-fn))

    (-> groups
        (.select "foreignObject")
        (.attr "width" fo-width-fn)
        (.attr "x" fo-x-fn))

    (-> groups
        (.select ".label")
        (.text (fn [^js d] (.. d -data -label))))

    (-> groups
        (.select ".label-ext")
        (.text (fn [^js d] (aget (.-data d) "label-ext"))))

    (-> groups
        (.select ".label-content")
        (.style "justify-content" align-fn))))

(defn- svg-bounds
  "Compute visible area in viewBox coordinates from the SVG element's
   actual pixel size. With preserveAspectRatio='xMidYMid meet' the
   viewBox scales uniformly to fit the smaller dimension — the larger
   axis gets extra visible space beyond the nominal viewBox."
  [svg-element {:keys [width height center-y-offset]}]
  (let [cw    (.-clientWidth svg-element)
        ch    (.-clientHeight svg-element)
        scale (if (and (pos? cw) (pos? ch))
                (min (/ cw width) (/ ch height))
                1)
        vw    (/ cw scale)
        vh    (/ ch scale)]
    {:half-w (/ vw 2)
     :top    (- (/ vh 2) center-y-offset)
     :bottom (+ (/ vh 2) center-y-offset)}))

(defn- calc-label-positions
  "Compute collision-free label positions using D3 force simulation."
  [svg-element config {:keys [data-ready radius]}]
  (let [;; distance from center to label anchor circle
        ring-radius (* radius (:label-ring-multiplier config))
        ;; arc generator for computing centroids on that circle
        ring-arc    (arc-at-radius ring-radius)
        nodes       (into-array
                     (for [^js d data-ready]
                       (let [[x y] (centroid ring-arc d)]
                         #js {:x       x
                              :y       y
                              :label   (.. d -data -label)
                              :targetX x
                              :targetY y})))]
    (sim/run-label-simulation nodes config {:ring-radius ring-radius
                                            :bounds      (svg-bounds svg-element config)})
    (into {}
          (map (fn [^js node]
                 [(.-label node)
                  {:x      (.-x node)
                   :y      (.-y node)
                   ;; Which side of the pie the label sits on.
                   ;; Determines text alignment
                   ;;   right-side → text starts at connector, grows rightward
                   ;;   left-side  → text ends at connector, grows leftward
                   :right? (pos? (.-x node))}]))
          nodes)))

;; ============================================================================
;; Main
;; ============================================================================

(defn render-chart!
  "Main render function. Orchestrates the full chart rendering.
   config — static tuning values (dimensions, multipliers, durations, etc.)
   ctx    — computed per-render values (data-ready, arc-generator, radius, etc.)"
  [svg-element {:chart/keys [data]} config]
  (let [radius (calc-radius config)

        ;; Setup SVG and get centered group
        svg (setup-svg! svg-element config)

        ;; Arc generator depends on radius so it's created per render
        arc-generator (create-arc-generator 0 radius)

        ;; Apply pie layout
        data-ready ((pie-gen (:pad-angle config)) (clj->js data))

        ;; Render context: computed values that change per render
        ctx {:data-ready      data-ready
             :arc-generator   arc-generator
             :radius          radius
             :tooltip         (:tooltip config)}

        ;; Calculate label positions using force simulation
        label-positions (calc-label-positions svg-element config ctx)

        ctx (assoc ctx :label-positions label-positions)

        ;; Wrapper group for slices + edges — carries the drop-shadow filter
        ;; so labels and polylines (rendered in svg) stay shadow-free.
        chart-body (-> (.selectAll svg "g.chart-body")
                     (.data #js [nil])
                     (.join "g")
                     (.attr "class" "chart-body"))]

    (render-edge! chart-body config ctx)
    (render-slices! chart-body config ctx)
    (render-polylines! svg config ctx)
    (render-labels! svg config ctx)))

;; ============================================================================
;; Export
;; ============================================================================

(defn- foreignobjects->text!
  "Replace foreignObject labels with SVG <text> so labels render
   correctly in print/export without external CSS."
  [svg]
  (doseq [group (array-seq (.querySelectorAll svg "g.label-group"))]
    (when-let [fo (.querySelector group "foreignObject")]
      (let [label-el  (.querySelector fo ".label")
            ext-el    (.querySelector fo ".label-ext")
            label-txt (when label-el (.-textContent label-el))
            ext-txt   (when ext-el (.-textContent ext-el))
            right?    (= "0" (.getAttribute fo "x"))
            text-el   (.createElementNS js/document "http://www.w3.org/2000/svg" "text")]
        (.setAttribute text-el "text-anchor" (if right? "start" "end"))
        (.setAttribute text-el "dominant-baseline" "central")
        (.setAttribute text-el "font-family" "Quicksand, sans-serif")
        (.setAttribute text-el "font-size" "12")
        (.setAttribute text-el "font-weight" "400")
        (when (seq label-txt)
          (let [tspan (.createElementNS js/document "http://www.w3.org/2000/svg" "tspan")]
            (set! (.-textContent tspan) label-txt)
            (.appendChild text-el tspan)))
        (when (seq ext-txt)
          (let [tspan (.createElementNS js/document "http://www.w3.org/2000/svg" "tspan")]
            (set! (.-textContent tspan) (str " " ext-txt))
            (.appendChild text-el tspan)))
        (.removeChild group fo)
        (.appendChild group text-el)))))

(defn svg-for-export
  "Clone SVG and prepare for export: replace foreignObject labels with
   SVG <text>, expand viewBox, remove --svg-scale."
  [svg-element]
  (let [svg-clone (rh/clone-svg svg-element)
        bbox      (.getBBox svg-element)
        pad       50
        bx        (- (.-x bbox) pad)
        by        (- (.-y bbox) pad)
        bw        (+ (.-width bbox) (* 2 pad))
        bh        (+ (.-height bbox) (* 2 pad))]
    (.removeProperty (.-style svg-clone) "--svg-scale")
    (foreignobjects->text! svg-clone)
    (rh/embed-styles! svg-element svg-clone [".connector"])
    (.setAttribute svg-clone "viewBox" (str bx " " by " " bw " " bh))
    (.setAttribute svg-clone "width" bw)
    (.setAttribute svg-clone "height" bh)
    svg-clone))
